// Add / edit manager dialog. Role (management-trade dropdown) and full name
// are set at creation and locked in edit mode; the phone is the only editable
// field afterwards. Name accepts anything but punctuation-only strings.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IAdminManager, IManagementTrade } from '@apis/adminManagers';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import { Field } from '@components/ui/Field/Field';
import { SingleSelect } from '@components/ui/SingleSelect/SingleSelect';
import { PhoneField } from '@components/ui/PhoneField/PhoneField';
import type { ISelectionOption } from '@components/Pages/AnalyticsPage/analytics.types';
import { tradeLabel } from '@utils/tradeLabel';
import styles from './ManagerDialog.module.css';

export interface IManagerDraft {
  tradeId: string;
  name: string;
  phone: string;
}

// Reject names composed only of punctuation.
const PUNCTUATION_ONLY_RE = /^[!@#$%&*()_.+=|<>?{}[\]~\\-\s]+$/;

interface IManagerDialogProps {
  open: boolean;
  manager: IAdminManager | null; // null → create; set → edit (role + name locked).
  trades: IManagementTrade[];
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (draft: IManagerDraft) => void;
}

export function ManagerDialog({
  open,
  manager,
  trades,
  submitting,
  onClose,
  onSubmit,
}: IManagerDialogProps) {
  const { t } = useTranslation();
  const isEdit = manager !== null;

  const [tradeId, setTradeId] = useState<string | null>(manager?.tradeId ?? null);
  const [name, setName] = useState(manager?.name ?? '');
  const [phone, setPhone] = useState(manager?.phoneNumber ?? '');
  const [phoneHasError, setPhoneHasError] = useState(false);

  const tradeOptions = useMemo<ISelectionOption[]>(
    () =>
      isEdit && manager.tradeId
        ? [{ id: manager.tradeId, label: tradeLabel(t, manager.tradeId) }]
        : trades.map((trade) => ({ id: trade.tradeId, label: tradeLabel(t, trade.tradeId) })),
    [isEdit, manager, trades, t],
  );

  const nameValid = name.trim() !== '' && !PUNCTUATION_ONLY_RE.test(name);
  // Edit: the Update button is always live (only a broken phone blocks);
  // create requires role + valid name.
  const canSubmit =
    !phoneHasError && !submitting && (isEdit || (tradeId !== null && nameValid));

  function handleClose() {
    setTradeId(manager?.tradeId ?? null);
    setName(manager?.name ?? '');
    setPhone(manager?.phoneNumber ?? '');
    setPhoneHasError(false);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isEdit ? t('adminManagement.dialog.editTitle') : t('adminManagement.dialog.createTitle')}
      footer={
        <>
          <span className={styles.footerSpacer} />
          <Button variant="secondary" size="md" onClick={handleClose}>
            {t('adminManagement.dialog.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={!canSubmit}
            onClick={() =>
              onSubmit({ tradeId: tradeId!, name: name.trim(), phone: phone.trim() })
            }
          >
            {isEdit
              ? t('adminManagement.dialog.editSubmit')
              : t('adminManagement.dialog.createSubmit')}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <div className={styles.formField}>
          <span className={styles.formLabel}>
            {t('adminManagement.field.role')}
            <span className={styles.requiredMark} aria-hidden="true">
              *
            </span>
          </span>
          <SingleSelect
            value={tradeId}
            onChange={(next) => setTradeId(next)}
            options={tradeOptions}
            placeholder={t('adminManagement.field.rolePlaceholder')}
            ariaLabel={t('adminManagement.field.role')}
            disabled={isEdit}
            elevated
          />
        </div>

        <Field
          label={t('adminManagement.field.fullName')}
          requiredMark
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('adminManagement.field.fullNamePlaceholder')}
          required
          disabled={isEdit}
          maxLength={200}
        />

        <PhoneField
          label={t('adminManagement.field.phone')}
          value={phone}
          onChange={setPhone}
          invalidMessage={t('adminManagement.field.phoneInvalid')}
          onErrorChange={setPhoneHasError}
        />
      </div>
    </Modal>
  );
}